import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { ProgressNoteDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { HOSPITAL_ABBREV_PROMPT, PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT } from "@/src/models/constants/prompt";
import { SERVICE_LINE_PROMPT, ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { diagnosisListSchema } from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const progressNoteTypeDisplay: Record<ProgressNoteDocumentType, string> = {
  [ProgressNoteDocumentType.CONTINUITY_OF_CARE]: "Continuity of Care",
  [ProgressNoteDocumentType.ER_REPORT]: "Hospital - ER Reports",
  [ProgressNoteDocumentType.ER_SUMMARY]: "Hospital - ER Summary of Care",
  [ProgressNoteDocumentType.FLOW_SHEET]: "Flow Sheet",
  [ProgressNoteDocumentType.HOSPITAL_DISCHARGE_SUMMARY]: "Hospital - Discharge Summary",
  [ProgressNoteDocumentType.HOSPITAL_OFFICE_VISIT]: "Hospital - Attending Phys Statement",
  [ProgressNoteDocumentType.HOSPITAL_RECORDS]: "Hospital Progress Note",
  [ProgressNoteDocumentType.HOSPITAL_SUMMARY]: "Hospital - Summary of Care",
  [ProgressNoteDocumentType.OFFICE_VISIT]: "Progress Note/Consult",
  [ProgressNoteDocumentType.OPERATIVE_REPORT]: "Operative Report",
  [ProgressNoteDocumentType.PRE_OPERATIVE]: "Pre-Operative Bundle",
  [ProgressNoteDocumentType.PROGRESS_NOTE]: "Progress Note",
  [ProgressNoteDocumentType.PT]: "PT/OT/ST",
  [ProgressNoteDocumentType.URGENT_CARE]: "Urgent Care Report",
  [ProgressNoteDocumentType.VITAL_CHART]: "Vitals Log",
};

export const progressNoteTypeSchema = z.nativeEnum(ProgressNoteDocumentType);

export enum ProgressNoteType {
  PT = "PT",
  OT = "OT",
  ST = "ST",
}

export enum SpecialtyFolder {
  ALLERGY_IMMUNOLOGY = "Allergy Immunology",
  CARDIOLOGY = "Cardiology",
  DERMATOLOGY = "Dermatology",
  ENDOCRINOLOGY = "Endocrinology",
  ENT = "ENT",
  GENERAL_SURGERY = "General Surgery",
  GI = "GI",
  GYN = "GYN",
  HEM_ONC = "Hem-Onc",
  NEPHROLOGY = "Nephrology",
  NEUROLOGY = "Neurology",
  NEUROSURGERY = "Neurosurgery",
  // TODO: dsy fix this to be Ophthalmology
  OPTHALMOLOGY = "Opthalmology", // This is wrong on purpose to match the Pinnacle list
  ORTHO = "Ortho",
  PAIN_MANAGEMENT = "Pain Management",
  PHYSICAL_THERAPY = "Physical Therapy",
  PODIATRY = "Podiatry",
  PSYCHIATRY_MENTAL_HEALTH = "Psychiatry-Mental Health",
  PULMONOLOGY = "Pulmonology",
  RHEUMATOLOGY = "Rheumatology",
  SLEEP_MEDICINE = "Sleep Medicine",
  UNCLEAR = "Unclear",
  UROLOGY = "Urology",
  VASCULAR = "Vascular",
  ANNUAL_WELLNESS_VISITS_AHA = "Annual Wellness Visits-AHA",
  INSURANCE_WELLNESS_VISITS = "Insurance Wellness Visits",
  LONG_TERM_CARE_FACILITY = "Long Term Care Facility",
  MISCELLANEOUS_CONSULT_NOTES = "Miscellaneous Consult Notes",
}

export enum ProgressNoteFlowSheetType {
  DIALYSIS = "Dialysis",
  RESPIRATORY = "Respiratory",
  OTHER = "Other",
}

const baseProgressNoteFields = {
  patient: patientMinimalSchema,
  dateOfProgressNote: simpleDateSchema
    .nullable()
    .describe(
      "Date of progress note. Ensure you find it within the progress note information and not from the document header. Format as YYYY-MM-DD."
    ),
  receivingLocation: locationSchema.nullable(),
};

const extendedProgressNoteSchema = z.object({
  specialty: z.nativeEnum(Specialty).nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  specialtyFolder: z.nativeEnum(SpecialtyFolder).nullable(),
  location: locationSchema.nullable(),
  reasonForVisit: z
    .string()
    .nullable()
    .describe(
      "Extract the chief complaint, reason for visit, or primary reason the patient was seen. " +
        'Look for headings like "Chief Complaint", "Reason for Visit", "CC", "Assessment", or "Impression". ' +
        "Return a concise summary, not the full assessment."
    ),
  reasonForReferral: z
    .string()
    .nullable()
    .describe(
      "Reason for referral, when the progress note documents a referral. Look for headings like " +
        '"Reason for Referral", "Referral Reason", or narrative cues such as "Referred for" or ' +
        '"For evaluation of". Return a concise phrase without headings or extra punctuation.'
    ),
  diagnosisList: diagnosisListSchema.nullable(),
  clinicName: z.string().nullable(),
  companyName: z.string().nullable(),
  documentType: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  progressNoteType: z.nativeEnum(ProgressNoteType).nullable(),
  encounterType: z.string().nullable().describe(PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT),
  orderNumber: z.string().nullable(),
  surgeryName: z.string().nullable(),
  hospitalAbbrev: z.string().nullable().describe(HOSPITAL_ABBREV_PROMPT),
  signatureRequired: z.boolean().nullable(),
  flowSheetType: z.nativeEnum(ProgressNoteFlowSheetType).nullable(),
  isSummaryOfCare: z.boolean().nullable(),
  serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  locationName: z.string().nullable(),
  isOperation: z.boolean().nullable(),
  isConsult: z.boolean().nullable(),
  isTelemedicine: z.boolean().nullable(),
  containsAdmissionLabel: z.boolean().nullable(),
  isDischarge: z.boolean().nullable(),
  isIfsp: z.boolean().nullable(),
  procedure: z.string().nullable(),
  ...DocumentDateSchema.shape,
});

const progressNoteCustomFields: Record<ProgressNoteDocumentType, Record<string, z.ZodTypeAny>> = {
  [ProgressNoteDocumentType.PT]: extendedProgressNoteSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    documentType: true,
    progressNoteType: true,
    encounterType: true,
    orderNumber: true,
    signatureRequired: true,
    receivingProvider: true,
    companyName: true,
  }).shape,
  [ProgressNoteDocumentType.OFFICE_VISIT]: extendedProgressNoteSchema.pick({
    specialtyFolder: true,
    dateOfEvent: true,
    location: true,
    reasonForVisit: true,
    diagnosisList: true,
    receivingProvider: true,
    clinicName: true,
  }).shape,
  [ProgressNoteDocumentType.ER_REPORT]: extendedProgressNoteSchema.pick({
    location: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
    clinicName: true,
  }).shape,
  [ProgressNoteDocumentType.HOSPITAL_OFFICE_VISIT]: extendedProgressNoteSchema.pick({
    specialty: true,
    dateOfEvent: true,
    receivingProvider: true,
  }).shape,
  [ProgressNoteDocumentType.HOSPITAL_RECORDS]: extendedProgressNoteSchema.pick({
    location: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
    clinicName: true,
  }).shape,
  [ProgressNoteDocumentType.HOSPITAL_DISCHARGE_SUMMARY]: extendedProgressNoteSchema.pick({
    location: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
    clinicName: true,
  }).shape,
  [ProgressNoteDocumentType.ER_SUMMARY]: extendedProgressNoteSchema.pick({
    location: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
  }).shape,
  [ProgressNoteDocumentType.HOSPITAL_SUMMARY]: extendedProgressNoteSchema.pick({
    location: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
  }).shape,
  [ProgressNoteDocumentType.OPERATIVE_REPORT]: extendedProgressNoteSchema.pick({
    dateOfEvent: true,
    surgeryName: true,
    provider: true,
    receivingProvider: true,
    specialtyFolder: true,
    signatureRequired: true,
    companyName: true,
    procedure: true,
  }).shape,
  [ProgressNoteDocumentType.PRE_OPERATIVE]: extendedProgressNoteSchema.pick({
    dateOfEvent: true,
    surgeryName: true,
    provider: true,
    receivingProvider: true,
  }).shape,
  [ProgressNoteDocumentType.URGENT_CARE]: extendedProgressNoteSchema.pick({
    dateOfEvent: true,
    receivingProvider: true,
  }).shape,
  [ProgressNoteDocumentType.PROGRESS_NOTE]: extendedProgressNoteSchema.shape,
  [ProgressNoteDocumentType.FLOW_SHEET]: extendedProgressNoteSchema.shape,
  [ProgressNoteDocumentType.CONTINUITY_OF_CARE]: extendedProgressNoteSchema.extend({
    companyName: z.string().nullable(),
  }).shape,
  [ProgressNoteDocumentType.VITAL_CHART]: extendedProgressNoteSchema.pick({
    dateOfEvent: true,
    receivingProvider: true,
  }).shape,
};

const makeProgressNoteSchema = (type: ProgressNoteDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseProgressNoteFields,
    ...customFields,
  });

export const progressNoteSchemas: Record<ProgressNoteDocumentType, z.ZodObject<any>> = (
  Object.values(ProgressNoteDocumentType) as ProgressNoteDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeProgressNoteSchema(type, progressNoteCustomFields[type]);

    return acc;
  },
  {} as Record<ProgressNoteDocumentType, z.ZodObject<any>>
);

const progressNoteSchemasArray = (Object.values(ProgressNoteDocumentType) as ProgressNoteDocumentType[]).map(
  (type) => progressNoteSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyProgressNoteSchema = z.discriminatedUnion("type", progressNoteSchemasArray);

export type ProgressNote = z.infer<typeof anyProgressNoteSchema> & DatabaseDefaults;

const now = new Date().toISOString();

export const progressNoteShape: ProgressNote = {
  id: "",
  type: ProgressNoteDocumentType.PROGRESS_NOTE,
  patient: null,
  dateOfProgressNote: null,
  specialty: null,
  provider: null,
  receivingProvider: null,
  specialtyFolder: null,
  location: null,
  receivingLocation: null,
  reasonForVisit: null,
  reasonForReferral: null,
  diagnosisList: null,
  clinicName: null,
  companyName: null,
  documentType: null,
  facilityType: null,
  progressNoteType: null,
  encounterType: null,
  orderNumber: null,
  surgeryName: null,
  hospitalAbbrev: null,
  signatureRequired: null,
  flowSheetType: null,
  isSummaryOfCare: null,
  serviceLine: null,
  locationName: null,
  isOperation: null,
  isConsult: null,
  isTelemedicine: null,
  isDischarge: null,
  isIfsp: null,
  dateOfDocument: null,
  dateOfEvent: null,
  dateReceived: null,
  dateSigned: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
